import {
  AppBar,
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  ThemeProvider,
  Toolbar,
  Tooltip,
  createTheme,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AccountState, Roles } from "../../account/models/AccountModels";
import StartRegistrationDialog from "../../account/components/StartRegistrationDialog";
import LoginDialog from "../../account/components/login/LoginDialog";
import accountContext from "../../account/accountContext";
import { refreshAccount } from "../../account/accountSlice";
import { buildAccountSignInWithReturn } from "../../account/routes";
import ForgotPasswordDialog from "../../account/components/ForgotPasswordDialog";
import { useAppDispatch, useAppSelector } from "../hooks/sharedHooks";
import LogoutIcon from "../assets/icons/Logout";
import { SettingsOutlined, GpsFixed as GpsFixedIcon, EngineeringOutlined } from "@mui/icons-material";
import { CustomerSettingsState } from "../../customer/settings/models/models";
import { getCustomer, resetCustomer } from "../../customer/settings/customerSettingsSlice";
import GlobalSearch from "./GlobalSearch";
import useTranslation from "../hooks/useTranslation";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "../notifications/components/NotificationBell";
import MachineIdLogoDark from "../assets/logo/MachineIdLogoDark";
import LogoSmallIcon from "../assets/logo/MachineIdLogoSmall";
import BriefcaseIcon from "../icons/BriefcaseIcon";
import ServiceHubIcon from "../icons/ServiceHubIcon";
import ServiceHubIconActive from "../icons/ServiceHubIconActive";
import FlagIcon from "../icons/FlagIcon";
import SupplierCenter from "../icons/SupplierCenter";
import SupplierCenterActive from "../icons/SupplierCenterActive";
import SearchAndBuy from "../icons/SearchAndBuy";
import SearchAndBuyActive from "../icons/SearchAndBuyActive";
import useCompanyType from "../hooks/useCompanyType";
import { getPartnershipStats } from "../../customer/partnerships/customerPartnershipsSlice";
import { CustomerPartnershipsState } from "../../customer/partnerships/models/models";
import { getServiceRequestStats } from "../../customer/servicing/customerServicingSlice";
import { ServiceRequestsState } from "../../customer/servicing/models/models";
import { getOfferStats } from "../../customer/serviceOffers/customerOffersSlice";
import { OffersState } from "../../customer/serviceOffers/models/models";
import useDataRefresh from "../notifications/hooks/useDataRefresh";
import { TrackingState } from "../tracking/trackingSlice";
import LivePulseDot from "../../customer/serviceTracking/components/LivePulseDot";
import MidasLogo from "../../customer/midas/components/MidasLogo";
import { dt } from "../themes/redesignTokens";
import permissionContext from "../../core/context/permissionContext";
import { PermissionLevel, Modules } from "../../core/models/PermissionModels";

/* ─── nav structure ─── */
type CompanyTypeFilter = "operator" | "provider" | "all";

interface NavSection {
  key: string;
  path: string;
  matchPaths: string[];  // paths that activate this section
  requiredRole?: string | string[];
  visibleFor?: CompanyTypeFilter;  // company type visibility filter
  disabledFor?: CompanyTypeFilter[];  // show grayed out + "Coming Soon" tooltip for these types (hybrid never disabled)
  permissionModule?: string;  // permission module key — section hidden when user has None(0) for this module
  /** If set, section is hidden for users with any of these roles. */
  hiddenForRoles?: string[];
  subItems?: NavSubItem[];
}

interface NavSubItem {
  key: string;
  path: string;
  shortLabel: string;  // abbreviated label for inactive state
  requiredRole?: string | string[];
  visibleFor?: CompanyTypeFilter;  // company type visibility filter
  /** If set, user must have >= this permission level for the parent section's permissionModule. */
  minPermissionLevel?: number;
  /** Module to check for minPermissionLevel. Required if minPermissionLevel is set. */
  permissionModule?: string;
  /** If set, sub-item is hidden for users with any of these roles. */
  hiddenForRoles?: string[];
}

const customerNavSections: NavSection[] = [
  /* ─── Active sections (always functional for their visibleFor audience) ─── */
  {
    key: "customer.machinery",
    path: "/machinery",
    matchPaths: ["/machinery", "/machinery/catalog"],
    requiredRole: Roles.ALL_COMPANY_ROLES,
    visibleFor: "operator",
    permissionModule: "Machinery",
  },
  {
    key: "customer.serviceHub",
    path: "/service-board",
    matchPaths: ["/service-board", "/service-requests", "/service-offers", "/active-work"],
    requiredRole: Roles.ALL_COMPANY_ROLES,
    visibleFor: "all",
    permissionModule: "Servicing",
    subItems: [
      { key: "customer.serviceBoard", path: "/service-board", shortLabel: "Board", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "all" },
      { key: "customer.myServiceRequests", path: "/service-requests", shortLabel: "In Progress", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "operator" },
      { key: "customer.myOffers", path: "/service-offers", shortLabel: "Offers", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "provider" },
      { key: "customer.activeWork", path: "/active-work", shortLabel: "Active", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "provider" },
    ],
  },
  {
    key: "customer.businessNetwork",
    path: "/companies",
    matchPaths: ["/companies", "/partnerships"],
    requiredRole: Roles.ALL_COMPANY_ROLES,
    visibleFor: "all",
    permissionModule: "PartnerHub",
    subItems: [
      { key: "customer.partnerHub", path: "/companies", shortLabel: "Partners", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "all" },
      { key: "customer.partnerships", path: "/partnerships", shortLabel: "Invitations", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "all" },
    ],
  },
  {
    key: "customer.operations",
    path: "/operations",
    matchPaths: ["/operations"],
    requiredRole: Roles.ALL_COMPANY_ROLES,
    visibleFor: "operator",
    permissionModule: "Maintenance",
    subItems: [
      { key: "customer.serviceReports", path: "/operations/service-reports", shortLabel: "Reports", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "operator", hiddenForRoles: [Roles.WORKER] },
      { key: "customer.workBoard", path: "/operations/work-board", shortLabel: "Board", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "operator", hiddenForRoles: [Roles.WORKER] },
      { key: "customer.myTasks", path: "/operations/my-tasks", shortLabel: "Tasks", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "operator" },
      { key: "customer.analytics", path: "/operations/analytics", shortLabel: "Analytics", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "operator", hiddenForRoles: [Roles.WORKER] },
    ],
  },
  /* ─── Coming Soon sections (grayed out for non-hybrid users) ─── */
  {
    key: "customer.serviceTracking",
    path: "/service-tracking",
    matchPaths: ["/service-tracking"],
    requiredRole: Roles.ALL_COMPANY_ROLES,
    visibleFor: "all",
    permissionModule: "Servicing",
    disabledFor: ["operator", "provider"],  // only hybrid sees it active
  },
  {
    key: "customer.warehouseCenter",
    path: "/supplier",
    matchPaths: ["/supplier"],
    requiredRole: Roles.ALL_COMPANY_ROLES,
    visibleFor: "operator",  // hidden from providers entirely
    permissionModule: "Inventory",
    disabledFor: ["operator"],  // operators see it grayed out; hybrid sees it active
    hiddenForRoles: [Roles.WORKER],
    subItems: [
      { key: "customer.myWarehouse", path: "/supplier/inventory", shortLabel: "Warehouse", requiredRole: Roles.ALL_COMPANY_ROLES, visibleFor: "operator" },
    ],
  },
  {
    key: "customer.marketplace",
    path: "/search",
    matchPaths: ["/search"],
    visibleFor: "operator",  // hidden from providers entirely
    permissionModule: "Trading",
    disabledFor: ["operator"],  // operators see it grayed out; hybrid sees it active
    hiddenForRoles: [Roles.WORKER],
  },
];

/* --- icon helper --- */
const getNavIcon = (sectionKey: string, active: boolean) => {
  const color = active ? dt.blue : dt.textSecondary;
  switch (sectionKey) {
    case "customer.machinery":
      return <BriefcaseIcon color={color} />;
    case "customer.serviceHub":
      return active ? <ServiceHubIconActive /> : <ServiceHubIcon />;
    case "customer.businessNetwork":
      return <FlagIcon color={color} />;
    case "customer.warehouseCenter":
      return active ? <SupplierCenterActive /> : <SupplierCenter />;
    case "customer.serviceTracking":
      return <GpsFixedIcon sx={{ fontSize: "20px", color: active ? "#10B981" : color }} />;
    case "customer.marketplace":
      return active ? <SearchAndBuyActive /> : <SearchAndBuy />;
    case "customer.operations":
      return <EngineeringOutlined sx={{ fontSize: "20px", color }} />;
    default:
      return null;
  }
};

const Header = () => {
  const { translate } = useTranslation(["common", "account", "layout"]);
  const { user } = useAppSelector<AccountState>((state: any) => state.account);
  const { customer } = useAppSelector<CustomerSettingsState>((state: any) => state.customerSettings);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const isCompact = useMediaQuery("(max-width:1200px)");
  const isPhone = useMediaQuery("(max-width:768px)");

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [isRegistrationDialog, setIsRegistrationDialog] = useState(false);
  const [isLoginDialog, setIsLoginDialog] = useState(false);
  const [isForgotPasswordDialog, setIsForgotPasswordDialog] = useState(false);

  const { pathname } = useLocation();
  const isAdmin = pathname.includes("admin");

  // Keyframe injection
  useEffect(() => {
    const styleId = "nav-redesign-keyframes";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes navSlideDown {
          0% { opacity: 0; max-height: 0; }
          100% { opacity: 1; max-height: 50px; }
        }
        @keyframes navFadeIn {
          0% { opacity: 0; transform: translateY(-4px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes navPulse {
          0% { transform: scale(0.6); opacity: 0; }
          60% { transform: scale(1.15); }
          100% { transform: scale(1); opacity: 1; }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const isProductsSearch = (): boolean => {
    return location.pathname.startsWith("/search");
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    accountContext.logout();
    dispatch(refreshAccount(null));
    setAnchorEl(null);
    const currentUrl = `${location.pathname}${location.search}${location.hash}`;
    navigate(buildAccountSignInWithReturn(currentUrl));
  };

  const handleToRegistration = () => {
    setIsLoginDialog(false);
    setIsRegistrationDialog(true);
  };

  const handleToForgotPassword = () => {
    setIsLoginDialog(false);
    setIsForgotPasswordDialog(true);
  };

  const handleToLogin = () => {
    setIsRegistrationDialog(false);
    setIsLoginDialog(true);
    setIsForgotPasswordDialog(false);
  };

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            paddingLeft: 16,
            paddingRight: 16,
            zIndex: 100,
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            "&.MuiMenuItem-root": {
              padding: "8px 16px",
            },
          },
        },
      },
    },
  });

  useEffect(() => {
    if (user?.role && Roles.ALL_COMPANY_ROLES.includes(user.role)) {
      // Guard module-specific API calls by permission to prevent 403s for restricted roles (e.g. Worker)
      const hasClaim = permissionContext.hasPermissionsClaim();
      const canPartnerHub = !hasClaim || permissionContext.hasPermission(Modules.PartnerHub, PermissionLevel.Read);
      const canSettings = !hasClaim || permissionContext.hasPermission(Modules.Settings, PermissionLevel.Read);
      const canServicing = !hasClaim || permissionContext.hasPermission(Modules.Servicing, PermissionLevel.Read);
      // GET /v1/customers requires PartnerHub:Read on the backend controller.
      // Only fetch if user has PartnerHub or Settings access (Workers may have neither).
      if (canPartnerHub || canSettings) {
        dispatch(getCustomer());
      }
      // Fetch partnership stats for pending invitation badge (lightweight GET)
      if (canPartnerHub) {
        dispatch(getPartnershipStats());
      }
      // Fetch service request stats for "new offers" badge (lightweight GET)
      if (canServicing) {
        dispatch(getServiceRequestStats());
      }
      // Fetch offer stats for "total active offers" badge on My Offers tab
      if (canServicing) {
        dispatch(getOfferStats());
      }
    } else {
      dispatch(resetCustomer());
    }
  }, [user, dispatch]);

  /* ─── nav helpers ─── */
  const isSectionActive = (section: NavSection): boolean => {
    return section.matchPaths.some((p) => location.pathname.startsWith(p));
  };

  const isSubItemActive = (subItem: NavSubItem): boolean => {
    // For exact match on sub-items, check if the path matches exactly or starts with it
    // Special handling: /companies should match exactly, not /companies/company-groups
    if (subItem.path === "/companies") {
      return location.pathname === "/companies" || location.pathname === "/companies/";
    }
    return location.pathname.startsWith(subItem.path);
  };

  const { isOperator, isProvider, companyType } = useCompanyType();

  // Partnership stats for pending invitation badge
  const { stats: partnershipStats } = useAppSelector<CustomerPartnershipsState>(
    (state: any) => state.customerPartnerships
  );
  const pendingReceivedCount = partnershipStats?.pendingReceivedCount ?? 0;

  // Service request stats for "new offers" nav badge
  const { serviceRequestStats } = useAppSelector<ServiceRequestsState>(
    (state: any) => state.customerServicing
  );
  const requestsWithNewOffers = serviceRequestStats?.requestsWithNewOffers ?? 0;
  const totalRequests = serviceRequestStats?.totalRequests ?? 0;

  // Offer stats for "total active offers" badge on My Offers tab
  const { offerStats } = useAppSelector<OffersState>(
    (state: any) => state.customerOffers
  );
  const totalActiveOffers = offerStats?.totalActiveOffers ?? 0;

  // Re-fetch stats when new offers arrive via SignalR (only if user has Servicing access)
  useDataRefresh("myServiceRequests", () => {
    const canServicing = !permissionContext.hasPermissionsClaim() || permissionContext.hasPermission(Modules.Servicing, PermissionLevel.Read);
    if (canServicing) {
      dispatch(getServiceRequestStats());
    }
  });

  // Re-fetch offer stats when offer data changes via SignalR (only if user has Servicing access)
  useDataRefresh("myOffers", () => {
    const canServicing = !permissionContext.hasPermissionsClaim() || permissionContext.hasPermission(Modules.Servicing, PermissionLevel.Read);
    if (canServicing) {
      dispatch(getOfferStats());
    }
  });

  // Tracking active sessions count for GPS nav badge
  const { activeSessions: trackingActiveSessions } = useAppSelector<TrackingState>(
    (state: any) => state.tracking
  );
  const activeTrackingCount = trackingActiveSessions?.length ?? 0;

  // MIDAS bot enabled status for header icon
  const isBotEnabled = useAppSelector((state) => state.chatBot.isBotEnabled);

  const isVisibleForType = (filter?: CompanyTypeFilter): boolean => {
    if (!filter || filter === "all") return true;
    // If no company type in token (e.g. not logged in, or legacy account), show everything
    if (companyType === 0) return true;
    if (filter === "operator") return isOperator;
    if (filter === "provider") return isProvider;
    return true;
  };

  // Check if a section should be shown grayed-out with "Coming Soon" tooltip
  const isDisabledForType = (disabledFor?: CompanyTypeFilter[]): boolean => {
    if (!disabledFor || disabledFor.length === 0) return false;
    // Hybrid users (companyType === 3, both bits set) NEVER see disabled items
    if (isOperator && isProvider) return false;
    // Legacy/not-logged-in (companyType === 0) — show everything active (same as current behavior)
    if (companyType === 0) return false;
    // Check if current user type matches any of the disabledFor filters
    for (const filter of disabledFor) {
      if (filter === "operator" && isOperator) return true;
      if (filter === "provider" && isProvider) return true;
      if (filter === "all") return true;
    }
    return false;
  };

  const hasRequiredRole = (requiredRole?: string | string[]): boolean => {
    if (!requiredRole) return true;
    if (Array.isArray(requiredRole)) return requiredRole.includes(user?.role || '');
    return requiredRole === user?.role;
  };

  // Permission-based nav filtering: hide sections where user has None(0) for the module.
  // During transition: if JWT has no permissions claim, skip permission filtering (backward compat).
  const hasPermissionsClaim = permissionContext.hasPermissionsClaim();

  const hasMinPermission = (sub: NavSubItem): boolean => {
    if (!hasPermissionsClaim) return true;  // no permissions claim → show all (transition mode)
    if (sub.minPermissionLevel == null || !sub.permissionModule) return true;
    return permissionContext.getModulePermission(sub.permissionModule) >= sub.minPermissionLevel;
  };

  const filteredSections = customerNavSections
    .filter((s) => hasRequiredRole(s.requiredRole))
    .filter((s) => isVisibleForType(s.visibleFor))
    .filter((s) => !s.hiddenForRoles || !s.hiddenForRoles.includes(user?.role || ''))
    .filter((s) => {
      if (!hasPermissionsClaim) return true;  // no permissions claim → show all (transition mode)
      if (!s.permissionModule) return true;   // no module mapping → always visible
      return permissionContext.hasPermission(s.permissionModule, PermissionLevel.Read);
    })
    .map((s) => {
      if (!s.subItems) return s;
      return {
        ...s,
        subItems: s.subItems
          .filter((sub) => isVisibleForType(sub.visibleFor))
          .filter((sub) => hasMinPermission(sub))
          .filter((sub) => !sub.hiddenForRoles || !sub.hiddenForRoles.includes(user?.role || '')),
      };
    });

  const filteredActiveSection = filteredSections.find((s) => isSectionActive(s)) || null;
  // Don't show sub-nav for disabled (Coming Soon) sections
  const activeSectionDisabled = filteredActiveSection ? isDisabledForType(filteredActiveSection.disabledFor) : false;
  const hasSubNav = filteredActiveSection && !activeSectionDisabled && filteredActiveSection.subItems && filteredActiveSection.subItems.length > 0;

  // Pure machine owner (companyType === 1) = not provider, not hybrid
  const isPureOwner = companyType === 1;

  /* Resolve sub-nav label: handles owner-specific and renamed labels */
  const getSubNavLabel = (sub: NavSubItem, active: boolean): string => {
    // Service Board → "My Board" for pure owners (short and full label are the same)
    if (sub.key === "customer.serviceBoard" && isPureOwner) {
      return translate("layout:customer.myBoard");
    }
    // My Service Requests → "Service In Progress" for all
    if (sub.key === "customer.myServiceRequests") {
      return active ? translate("layout:customer.serviceInProgress") : sub.shortLabel;
    }
    // Default: full translation when active, shortLabel when inactive
    return active ? translate(`layout:${sub.key}`) : sub.shortLabel;
  };

  /* ─── nav item styles ─── */
  const navItemSx = (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: active ? "8px" : "0px",
    padding: isCompact ? "16px 10px" : active ? "18px 14px" : "18px 12px",
    fontFamily: dt.font,
    fontSize: isCompact ? "13px" : "14px",
    fontWeight: active ? 600 : 500,
    color: active ? dt.blue : dt.textSecondary,
    textDecoration: "none",
    borderBottom: active ? `2px solid ${dt.blue}` : "2px solid transparent",
    transition: "all 0.25s ease",
    whiteSpace: "nowrap" as const,
    cursor: "pointer",
    "&:hover": {
      color: dt.blueHover,
      backgroundColor: dt.blueSoft,
    },
  });

  const phoneNavItemSx = (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: active ? "6px" : "0px",
    padding: "10px 10px",
    fontFamily: dt.font,
    fontSize: "12px",
    fontWeight: active ? 600 : 500,
    color: active ? dt.blue : dt.textSecondary,
    textDecoration: "none",
    borderBottom: active ? `2px solid ${dt.blue}` : "2px solid transparent",
    transition: "all 0.25s ease",
    whiteSpace: "nowrap" as const,
    cursor: "pointer",
    "&:hover": {
      color: dt.blueHover,
      backgroundColor: dt.blueSoft,
    },
  });

  const subNavItemSx = (active: boolean) => ({
    display: "flex",
    alignItems: "center",
    padding: isPhone ? "10px 12px" : "12px 16px",
    fontFamily: dt.font,
    fontSize: isPhone ? "12px" : "13px",
    fontWeight: active ? 600 : 500,
    color: active ? dt.blue : dt.textSecondary,
    textDecoration: "none",
    borderBottom: active ? `2px solid ${dt.blue}` : "2px solid transparent",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap" as const,
    cursor: "pointer",
    "&:hover": {
      color: dt.blueHover,
      backgroundColor: dt.blueSoft,
    },
  });

  /* ─── render utilities section ─── */
  const renderUtilities = () => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        flexWrap: "nowrap",
        flexShrink: 1,
        minWidth: 0,
      }}
    >
      <LanguageSwitcher />

      {!user && (
        <div>
          <Button variant="contained" onClick={() => setIsLoginDialog(true)}>
            {translate("account:loginButton")}
          </Button>
          <Button
            variant="outlined"
            sx={{ marginLeft: "8px" }}
            onClick={() => setIsRegistrationDialog(true)}
          >
            {translate("account:registerButton")}
          </Button>
        </div>
      )}

      {user && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <button
            id="basic-button"
            aria-controls={open ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              minWidth: 0,
              border: "none",
              background: "none",
              cursor: "pointer",
              padding: 0,
              outline: "none",
            }}
            onClick={handleClick}
          >
            {!isCompact && (
              <span
                style={{
                  fontSize: "14px",
                  fontFamily: dt.font,
                  color: dt.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "80px",
                }}
              >
                {user.firstName}
              </span>
            )}
            <div
              style={{
                width: "32px",
                height: "32px",
                minWidth: "32px",
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${dt.blue}, ${dt.blueHover})`,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: "13px",
                fontFamily: dt.font,
              }}
            >
              {user.firstName?.charAt(0)?.toUpperCase()}
            </div>
          </button>

          {/* MIDAS assistant icon — only when bot is enabled AND user has Midas permission */}
          {isBotEnabled && (!hasPermissionsClaim || permissionContext.hasPermission("Midas", PermissionLevel.Read)) && (
            <Link
              to="/midas"
              style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
              title={translate("common:navigation.midas")}
            >
              <MidasLogo size="sm" style={{ cursor: "pointer" }} />
            </Link>
          )}

          <NotificationBell />
        </div>
      )}
    </div>
  );

  /* ─── ADMIN HEADER (unchanged) ─── */
  if (isAdmin) {
    return (
      <ThemeProvider theme={theme}>
        <AppBar position="static" elevation={0} sx={{ zIndex: 1250, overflow: "visible", height: "auto" }}>
          <Toolbar className="flex justify-between">
            <p className="heading-1">{translate("layout:header.adminPanel")}</p>

            <div className="flex items-center gap-4">
              <LanguageSwitcher />

              {user && (
                <div className="flex">
                  <button
                    id="basic-button"
                    aria-controls={open ? "basic-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? "true" : undefined}
                    className="flex flex-col"
                    onClick={handleClick}
                  >
                    <p className="text-12-medium">
                      {translate("common.hello")}, {user.firstName}
                    </p>

                    <div className="flex">
                      <p className="text-14-medium" style={{ color: "#333" }}>
                        {translate("navigation.account")}
                      </p>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </Toolbar>
        </AppBar>

        {/* User dropdown menu */}
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{ "aria-labelledby": "basic-button" }}
        >
          <Box className="text-16-bold px-4 py-2" color={"#333333"}>
            {user?.firstName}
          </Box>

          {customer && (
            <Box>
              <NavLink to="/settings/profile">
                <MenuItem onClick={handleClose}>
                  <SettingsOutlined sx={{ mr: 1, fontSize: "18px", color: dt.textSecondary }} />
                  {translate("common:navigation.settings")}
                </MenuItem>
              </NavLink>
            </Box>
          )}

          <MenuItem onClick={handleLogout}>
            {translate("navigation.logout")}
            <Box className="ml-2">
              <LogoutIcon />
            </Box>
          </MenuItem>
        </Menu>

        <StartRegistrationDialog
          key={`start-registration-dialog-${isRegistrationDialog}`}
          open={isRegistrationDialog}
          onToLogin={handleToLogin}
          onClose={() => setIsRegistrationDialog(false)}
        />
        <LoginDialog
          key={`login-dialog-${isLoginDialog}`}
          open={isLoginDialog}
          onToRegistration={handleToRegistration}
          onToForgotPassword={handleToForgotPassword}
          onClose={() => setIsLoginDialog(false)}
          returnUrl={`${location.pathname}${location.search}${location.hash}`}
        />
        <ForgotPasswordDialog
          key={`forgot-password-dialog-${isForgotPasswordDialog}`}
          open={isForgotPasswordDialog}
          onToLogin={handleToLogin}
          onClose={() => setIsForgotPasswordDialog(false)}
        />
      </ThemeProvider>
    );
  }

  /* ─── CUSTOMER HEADER (redesigned) ─── */
  return (
    <ThemeProvider theme={theme}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          zIndex: 1250,
          overflow: "visible",
          height: "auto",
          backgroundColor: dt.cardBg,
        }}
      >
        {/* ─── Row 1: Main navigation ─── */}
        <Toolbar
          className="cross-browser-fill-width"
          sx={{
            "&.MuiToolbar-root": {
              display: "flex",
              flexWrap: "nowrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "0px",
              minHeight: "unset !important",
              height: "auto !important",
              padding: "0 16px !important",
              borderBottom: `1px solid ${dt.cardBorder}`,
              overflow: "visible",
              background: dt.cardBg,
            },
          }}
        >
          {/* Left section: Logo + Nav items */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0px",
              minWidth: 0,
              flex: 1,
            }}
          >
            {/* Logo */}
            <Link
              to="/"
              style={{
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                marginRight: isCompact ? "8px" : "32px",
                flexShrink: 0,
                padding: "12px 0",
              }}
            >
              {isPhone ? <LogoSmallIcon /> : <MachineIdLogoDark />}
            </Link>

            {/* Desktop/Tablet nav items - icon only when inactive, icon+text when active */}
            {!isPhone && (
              <nav
                style={{
                  display: "flex",
                  alignItems: "stretch",
                  gap: "0px",
                  height: "100%",
                }}
              >
                {filteredSections.map((section, idx) => {
                  const active = isSectionActive(section);
                  const disabled = isDisabledForType(section.disabledFor);
                  const isTrackingNav = section.key === "customer.serviceTracking";
                  const navColor = isTrackingNav && active && !disabled ? "#10B981" : dt.blue;

                  // Insert divider before the first disabled section
                  const isFirstDisabled = disabled && (idx === 0 || !isDisabledForType(filteredSections[idx - 1]?.disabledFor));

                  const navContent = (
                    <Box sx={{
                      ...navItemSx(active && !disabled),
                      ...(isTrackingNav && active && !disabled ? { borderBottomColor: "#10B981" } : {}),
                      ...(disabled ? {
                        opacity: 0.45,
                        cursor: "default",
                        borderBottomColor: "transparent",
                        "&:hover": {
                          color: dt.textSecondary,
                          backgroundColor: "transparent",
                        },
                      } : {}),
                    }}>
                      {/* Icon always visible */}
                      <Box sx={{ display: "flex", alignItems: "center", width: "20px", height: "20px", flexShrink: 0, position: "relative" }}>
                        {disabled
                          ? getNavIcon(section.key, false)  // always show muted icon when disabled
                          : getNavIcon(section.key, active)
                        }
                        {/* LivePulseDot for tracking nav — only when NOT disabled */}
                        {isTrackingNav && activeTrackingCount > 0 && !active && !disabled && (
                          <Box sx={{ position: "absolute", top: -2, right: -4 }}>
                            <LivePulseDot size={6} color="#10B981" />
                          </Box>
                        )}
                      </Box>
                      {/* Text only when active and not disabled */}
                      {active && !disabled && (
                        <span style={{
                          fontFamily: dt.font,
                          fontSize: isCompact ? "13px" : "14px",
                          fontWeight: 600,
                          color: navColor,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                        }}>
                          {translate(`layout:${section.key}`)}
                        </span>
                      )}
                      {/* Active count badge for tracking — only when NOT disabled */}
                      {isTrackingNav && activeTrackingCount > 0 && active && !disabled && (
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          backgroundColor: "rgba(16,185,129,0.1)",
                          borderRadius: "10px",
                          padding: "1px 6px",
                        }}>
                          <LivePulseDot size={5} color="#10B981" />
                          <Box component="span" sx={{
                            fontFamily: dt.fontMono,
                            fontSize: "10px",
                            fontWeight: 700,
                            color: "#10B981",
                            lineHeight: "16px",
                          }}>
                            {activeTrackingCount}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );

                  return (
                    <React.Fragment key={section.key}>
                      {/* Divider before first coming-soon section */}
                      {isFirstDisabled && (
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          px: "4px",
                        }}>
                          <Divider orientation="vertical" flexItem sx={{
                            borderColor: dt.cardBorder,
                            borderRightWidth: "1px",
                            my: "14px",
                          }} />
                        </Box>
                      )}
                      {disabled ? (
                        <Tooltip
                          title={translate("layout:comingSoon")}
                          arrow
                          enterTouchDelay={300}
                          leaveTouchDelay={1500}
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontFamily: dt.font,
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor: dt.textPrimary,
                                borderRadius: "6px",
                                padding: "6px 12px",
                                letterSpacing: "0.3px",
                              },
                            },
                            arrow: {
                              sx: { color: dt.textPrimary },
                            },
                          }}
                        >
                          <Box
                            onClick={(e: React.MouseEvent) => e.preventDefault()}
                            sx={{ textDecoration: "none", cursor: "default" }}
                          >
                            {navContent}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Link
                          to={section.path}
                          style={{ textDecoration: "none" }}
                        >
                          {navContent}
                        </Link>
                      )}
                    </React.Fragment>
                  );
                })}
              </nav>
            )}

            {/* Global search for /search page */}
            {!isPhone && isProductsSearch() && location.search && (
              <Box sx={{ ml: 2, flex: 1, maxWidth: "400px" }}>
                <GlobalSearch width="100%" />
              </Box>
            )}
          </div>

          {/* Right section: Utilities */}
          {renderUtilities()}
        </Toolbar>

        {/* --- Row 2 (phone only): Nav items - icon only when inactive, icon+text when active --- */}
        {isPhone && (
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              borderBottom: `1px solid ${dt.cardBorder}`,
              overflowX: "auto",
              WebkitOverflowScrolling: "touch",
              px: 1,
              backgroundColor: dt.cardBg,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {filteredSections.map((section, idx) => {
              const active = isSectionActive(section);
              const disabled = isDisabledForType(section.disabledFor);
              const isTrackingNav = section.key === "customer.serviceTracking";
              const navColor = isTrackingNav && active && !disabled ? "#10B981" : dt.blue;

              // Insert divider before the first disabled section
              const isFirstDisabled = disabled && (idx === 0 || !isDisabledForType(filteredSections[idx - 1]?.disabledFor));

              const phoneNavContent = (
                <Box sx={{
                  ...phoneNavItemSx(active && !disabled),
                  ...(isTrackingNav && active && !disabled ? { borderBottomColor: "#10B981" } : {}),
                  ...(disabled ? {
                    opacity: 0.45,
                    cursor: "default",
                    borderBottomColor: "transparent",
                    "&:hover": {
                      color: dt.textSecondary,
                      backgroundColor: "transparent",
                    },
                  } : {}),
                }}>
                  {/* Icon always visible */}
                  <Box sx={{ display: "flex", alignItems: "center", width: "20px", height: "20px", flexShrink: 0, position: "relative" }}>
                    {disabled
                      ? getNavIcon(section.key, false)
                      : getNavIcon(section.key, active)
                    }
                    {/* LivePulseDot — only when NOT disabled */}
                    {isTrackingNav && activeTrackingCount > 0 && !active && !disabled && (
                      <Box sx={{ position: "absolute", top: -2, right: -4 }}>
                        <LivePulseDot size={5} color="#10B981" />
                      </Box>
                    )}
                  </Box>
                  {/* Text only when active and not disabled.
                      When the active section has a sub-nav row below, the sub-item label
                      already tells the user where they are — hide the section label on
                      phone to save ~22px of vertical chrome (icons-only section row). */}
                  {active && !disabled && !hasSubNav && (
                    <span style={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      fontWeight: 600,
                      color: navColor,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    }}>
                      {translate(`layout:${section.key}`)}
                    </span>
                  )}
                </Box>
              );

              return (
                <React.Fragment key={section.key}>
                  {/* Vertical divider before first coming-soon section (phone) */}
                  {isFirstDisabled && (
                    <Box sx={{
                      display: "flex",
                      alignItems: "center",
                      px: "3px",
                    }}>
                      <Divider orientation="vertical" flexItem sx={{
                        borderColor: dt.cardBorder,
                        borderRightWidth: "1px",
                        my: "10px",
                      }} />
                    </Box>
                  )}
                  {disabled ? (
                    <Tooltip
                      title={translate("layout:comingSoon")}
                      arrow
                      enterTouchDelay={300}
                      leaveTouchDelay={1500}
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontFamily: dt.font,
                            fontSize: "11px",
                            fontWeight: 600,
                            backgroundColor: dt.textPrimary,
                            borderRadius: "6px",
                            padding: "5px 10px",
                          },
                        },
                        arrow: {
                          sx: { color: dt.textPrimary },
                        },
                      }}
                    >
                      <Box
                        onClick={(e: React.MouseEvent) => e.preventDefault()}
                        sx={{ textDecoration: "none", cursor: "default" }}
                      >
                        {phoneNavContent}
                      </Box>
                    </Tooltip>
                  ) : (
                    <Link to={section.path} style={{ textDecoration: "none" }}>
                      {phoneNavContent}
                    </Link>
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}

        {/* --- Sub-navigation row (all screen sizes, conditional) --- */}
        {/* Sub-items: show shortLabel when inactive, full translated name when active */}
        {hasSubNav && (
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              backgroundColor: dt.headerBg,
              borderBottom: `1px solid ${dt.cardBorder}`,
              pl: "16px",
              animation: "navFadeIn 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              overflowX: isPhone ? "auto" : "hidden",
              WebkitOverflowScrolling: "touch",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {/* Offset for logo width alignment (desktop/tablet only) */}
            {!isPhone && <Box sx={{ width: isCompact ? "140px" : "191px", flexShrink: 0 }} />}
            <nav
              style={{
                display: "flex",
                alignItems: "stretch",
                gap: "0px",
              }}
            >
              {filteredActiveSection!.subItems!.map((sub) => {
                const subActive = isSubItemActive(sub);
                return (
                  <Link
                    key={sub.key}
                    to={sub.path}
                    style={{ textDecoration: "none" }}
                  >
                    <Box sx={subNavItemSx(subActive)}>
                      {getSubNavLabel(sub, subActive)}
                      {/* Pending invitation count badge */}
                      {sub.key === "customer.partnerships" && pendingReceivedCount > 0 && (
                        <Box
                          sx={{
                            ml: "6px",
                            fontSize: "10px",
                            fontFamily: dt.fontMono,
                            fontWeight: 700,
                            backgroundColor: dt.blue,
                            color: "#fff",
                            padding: "1px 6px",
                            borderRadius: "10px",
                            lineHeight: "16px",
                            animation: "navPulse 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          {pendingReceivedCount}
                        </Box>
                      )}
                      {/* Total active requests count badge (muted gray pill) */}
                      {sub.key === "customer.myServiceRequests" && totalRequests > 0 && (
                        <Box
                          sx={{
                            ml: "6px",
                            fontSize: "10px",
                            fontFamily: dt.fontMono,
                            fontWeight: 700,
                            backgroundColor: "#e0e0e0",
                            color: "#666",
                            padding: "1px 6px",
                            borderRadius: "10px",
                            lineHeight: "16px",
                          }}
                        >
                          {totalRequests}
                        </Box>
                      )}
                      {/* New offers count badge on Requests sub-nav */}
                      {sub.key === "customer.myServiceRequests" && requestsWithNewOffers > 0 && (
                        <Box
                          sx={{
                            ml: "6px",
                            fontSize: "10px",
                            fontFamily: dt.fontMono,
                            fontWeight: 700,
                            backgroundColor: dt.blue,
                            color: "#fff",
                            padding: "1px 6px",
                            borderRadius: "10px",
                            lineHeight: "16px",
                            animation: "navPulse 0.6s cubic-bezier(0.4, 0, 0.2, 1)",
                          }}
                        >
                          {requestsWithNewOffers}
                        </Box>
                      )}
                      {/* Total active offers count badge (muted gray pill) */}
                      {sub.key === "customer.myOffers" && totalActiveOffers > 0 && (
                        <Box
                          sx={{
                            ml: "6px",
                            fontSize: "10px",
                            fontFamily: dt.fontMono,
                            fontWeight: 700,
                            backgroundColor: "#e0e0e0",
                            color: "#666",
                            padding: "1px 6px",
                            borderRadius: "10px",
                            lineHeight: "16px",
                          }}
                        >
                          {totalActiveOffers}
                        </Box>
                      )}
                    </Box>
                  </Link>
                );
              })}
            </nav>
          </Box>
        )}

        {/* --- Mobile: Global search for /search (below header) --- */}
        {isPhone && isProductsSearch() && location.search && (
          <Box sx={{ px: 2, py: 1, backgroundColor: dt.cardBg, borderBottom: `1px solid ${dt.cardBorder}` }}>
            <GlobalSearch width="100%" />
          </Box>
        )}
      </AppBar>

      {/* ─── User dropdown menu ─── */}
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-labelledby": "basic-button" }}
      >
        <Box className="text-16-bold px-4 py-2" color={"#333333"}>
          {user?.firstName}
        </Box>

        {customer && (!hasPermissionsClaim || permissionContext.hasPermission("Settings", PermissionLevel.Read)) && (
          <Box>
            <NavLink to="/settings/profile">
              <MenuItem onClick={handleClose}>
                <SettingsOutlined sx={{ mr: 1, fontSize: "18px", color: dt.textSecondary }} />
                {translate("common:navigation.settings")}
              </MenuItem>
            </NavLink>
          </Box>
        )}

        <MenuItem onClick={handleLogout}>
          {translate("navigation.logout")}
          <Box className="ml-2">
            <LogoutIcon />
          </Box>
        </MenuItem>
      </Menu>

      {/* ─── Auth Dialogs ─── */}
      <StartRegistrationDialog
        key={`start-registration-dialog-${isRegistrationDialog}`}
        open={isRegistrationDialog}
        onToLogin={handleToLogin}
        onClose={() => setIsRegistrationDialog(false)}
      />
      <LoginDialog
        key={`login-dialog-${isLoginDialog}`}
        open={isLoginDialog}
        onToRegistration={handleToRegistration}
        onToForgotPassword={handleToForgotPassword}
        onClose={() => setIsLoginDialog(false)}
        returnUrl={`${location.pathname}${location.search}${location.hash}`}
      />
      <ForgotPasswordDialog
        key={`forgot-password-dialog-${isForgotPasswordDialog}`}
        open={isForgotPasswordDialog}
        onToLogin={handleToLogin}
        onClose={() => setIsForgotPasswordDialog(false)}
      />
    </ThemeProvider>
  );
};

export default Header;